import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Task, TaskPriority, TaskStatus } from '@/interfaces/Task'
import { groupBy } from 'lodash'

interface TasksState {
  error: string | null;
  loading: boolean;
  tasks: Map<string, Task>;
}

const initialState: TasksState = {
  tasks: new Map(),
  loading: false,
  error: null,
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    addTask: (state, action: PayloadAction<Omit<Task, 'id' | 'createdAt'>>) => {
      const newTask: Task = {
        id: crypto.randomUUID(),
        title: action.payload.title,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: TaskStatus.PENDING,
        priority: TaskPriority.MEDIUM,
        assignee: action.payload.assignee,
        project: action.payload.project,
      }
      state.tasks.set(newTask.id, newTask)
    },
    removeTask: (state, action: PayloadAction<string>) => {
      state.tasks.delete(action.payload)
    },
    updateTask: (state, action: PayloadAction<{ id: string; title: string }>) => {
      const existingTask = state.tasks.get(action.payload.id)
      if (existingTask) {
        state.tasks.set(action.payload.id, {
          ...existingTask,
          title: action.payload.title,
          updatedAt: new Date().toISOString(),
        })
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setTasks: (state, action: PayloadAction<Map<string, Task>>) => {
      state.tasks = action.payload
    },
  },
})

export const selectTasks = (state: { tasks: TasksState }) => state.tasks.tasks

export const selectTasksGroupedBy = (state: { tasks: TasksState }, group: keyof Task) => {
  const tasks = Array.from(state.tasks.tasks.values()).filter((task) => task[group] === group)
  return groupBy(tasks, group)
}

export const {
  addTask,
  removeTask,
  updateTask,
  setLoading,
  setError,
  setTasks,
} = tasksSlice.actions

export default tasksSlice.reducer
